import { EMPTY_TILE } from "../document/packed-tile.ts";
import type { LayerDocument, MapDocument, PropInstance, TerrainLayer, TileRef } from "../document/types.ts";
import { propAt, propRowAt, type FrameSizeOf, type OpaqueAt, type PropPick } from "./prop-hit.ts";
import { withTieKeys, type OrderedProp } from "./sort.ts";

/**
 * The select tool's hit-test (ADR-045): what sits under a world point across every usable layer, in the
 * order the screen shows it. Entity markers draw above everything, so they win; then props by opaque
 * pixel, topmost layer first; then the ground: the topmost tile or terrain layer that draws a tile in the cell
 * there, the two kinds searched together in draw order, so terrain painted over a base tile layer is what a
 * click on it finds (ADR-045 decision 3 as amended 2026-10-04).
 */

const ENTITY_REACH = 0.5;

/**
 * What the select tool found under the pointer.
 */
export type PickHit =
  | { readonly kind: "prop"; readonly layerId: string; readonly propId: string }
  | { readonly kind: "entity"; readonly layerId: string; readonly entityId: string }
  | { readonly kind: "tile"; readonly layerId: string; readonly x: number; readonly y: number }
  | { readonly kind: "terrain"; readonly layerId: string; readonly x: number; readonly y: number };

/**
 * How the select tool sees props across layers (P1.6-T41): `frameOf` is the frame the map draws a prop
 * with, so a roof shown open is hit by its open frame; `pickable` passes over the props it rejects, such as
 * every presence prop while the pick-through modifier is held. Absent, each prop is hit by its own frame
 * and every prop is pickable. `isPresence` names the props presence drives, so exact ties fall by the tie
 * key the map draws them by (P1.6-T42); absent, no prop is taken for one and ties fall by layer order.
 * `cellsOf` gives the packed tiles a terrain layer draws, one per cell (`EMPTY_TILE` where it draws nothing),
 * so a terrain layer is hit exactly where the screen shows it: not in a cell whose corners resolve to no tile,
 * and in the rows a rule or a face draws beyond its corners. Absent, or null for a layer, the layer is hit in
 * every cell with at least one of its four corner vertices painted.
 */
export interface PickOptions {
  readonly frameOf?: (layerId: string, prop: PropInstance) => TileRef;
  readonly pickable?: (layerId: string, prop: PropInstance) => boolean;
  readonly isPresence?: (layerId: string, prop: PropInstance) => boolean;
  readonly cellsOf?: (layerId: string) => Readonly<Uint32Array> | null;
}

/**
 * A layer's props with their tie keys, as the map draws them (P1.6-T42).
 *
 * @param layerId The layer.
 * @param props Its props in document order.
 * @param options The options across layers, for which props presence drives.
 * @returns The props, each with its key.
 */
function keyed(layerId: string, props: readonly PropInstance[], options: PickOptions): OrderedProp[] {
  const { isPresence } = options;
  return withTieKeys(props, isPresence === undefined ? () => false : (prop) => isPresence(layerId, prop));
}

/**
 * The options for one layer's props, as `propAt` takes them.
 *
 * @param layerId The layer.
 * @param options The options across layers.
 * @returns The options bound to the layer.
 */
function forLayer(layerId: string, options: PickOptions): PropPick {
  const { frameOf, pickable } = options;
  return {
    ...(frameOf === undefined ? {} : { frameOf: (prop: PropInstance) => frameOf(layerId, prop) }),
    ...(pickable === undefined ? {} : { pickable: (prop: PropInstance) => pickable(layerId, prop) }),
  };
}

/**
 * Finds the topmost thing under a world point.
 *
 * @param map The map whose layers are searched in reverse draw order.
 * @param usable Whether a layer may be picked from (visible and unlocked).
 * @param sizeOf Frame size lookup for props.
 * @param opaque Frame pixel opacity lookup for props.
 * @param tileSize Tile size in pixels.
 * @param wx World pixel x.
 * @param wy World pixel y.
 * @param options The frame each prop is shown with, which props may be hit and which presence drives.
 * @returns The hit, or null when nothing usable lies under the point or it is off the map.
 */
export function pickAt(map: MapDocument, usable: (layerId: string) => boolean, sizeOf: FrameSizeOf, opaque: OpaqueAt, tileSize: number, wx: number, wy: number, options: PickOptions = {}): PickHit | null {
  const cx = Math.floor(wx / tileSize);
  const cy = Math.floor(wy / tileSize);
  if (cx < 0 || cy < 0 || cx >= map.width || cy >= map.height) {
    return null;
  }
  const tx = wx / tileSize;
  const ty = wy / tileSize;
  return (
    topmost(map.layers, usable, "entity", (layer) => {
      const entity = [...layer.entities].reverse().find((e) => Math.abs(e.x - tx) < ENTITY_REACH && Math.abs(e.y - ty) < ENTITY_REACH);
      return entity === undefined ? null : { kind: "entity", layerId: layer.id, entityId: entity.id };
    }) ??
    topmost(map.layers, usable, "prop", (layer) => {
      const hit = propAt(keyed(layer.id, layer.props, options), sizeOf, opaque, tileSize, wx, wy, forLayer(layer.id, options));
      return hit === undefined ? null : { kind: "prop", layerId: layer.id, propId: hit.id };
    }) ??
    groundAt(map, usable, cx, cy, options)
  );
}

/**
 * Whether a terrain layer draws a tile in a cell: by the cells it draws when the caller knows them, else by
 * its corners, any of the cell's four vertices painted.
 *
 * @param layer The terrain layer.
 * @param width The map's width in tiles.
 * @param cx Cell column.
 * @param cy Cell row.
 * @param options The options across layers, for the drawn cells.
 * @returns True when the layer shows something in the cell.
 */
function terrainDrawn(layer: TerrainLayer, width: number, cx: number, cy: number, options: PickOptions): boolean {
  const drawn = options.cellsOf?.(layer.id) ?? null;
  if (drawn !== null) {
    return (drawn[cy * width + cx] ?? EMPTY_TILE) !== EMPTY_TILE;
  }
  const stride = width + 1;
  const top = cy * stride + cx;
  const bottom = top + stride;
  return ((layer.corners[top] ?? 0) | (layer.corners[top + 1] ?? 0) | (layer.corners[bottom] ?? 0) | (layer.corners[bottom + 1] ?? 0)) !== 0;
}

/**
 * The ground under a cell: the topmost usable tile or terrain layer that draws a tile there, the two kinds
 * searched together from the top of the draw order down.
 *
 * @param map The map.
 * @param usable Whether a layer may be picked from.
 * @param cx Cell column, inside the map.
 * @param cy Cell row, inside the map.
 * @param options The options across layers, for the cells a terrain layer draws.
 * @returns The tile or terrain hit, or null when no usable layer draws the cell.
 */
function groundAt(map: MapDocument, usable: (layerId: string) => boolean, cx: number, cy: number, options: PickOptions): PickHit | null {
  for (let i = map.layers.length - 1; i >= 0; i -= 1) {
    const layer = map.layers[i] as LayerDocument;
    if ((layer.kind !== "tile" && layer.kind !== "terrain") || !usable(layer.id)) {
      continue;
    }
    if (layer.kind === "tile" ? layer.tiles[cy * map.width + cx] !== EMPTY_TILE : terrainDrawn(layer, map.width, cx, cy, options)) {
      return { kind: layer.kind, layerId: layer.id, x: cx, y: cy };
    }
  }
  return null;
}

/**
 * Every prop under a world point on the usable prop layers, in the order a stack of them is dug through
 * (ADR-066): the topmost layer first and, within a layer, the prop drawn on top first, the order `propAt`
 * prefers (the greatest sort row, then the greatest tie key, P1.6-T42). Transparent pixels hit nothing, as they
 * do for the select tool.
 *
 * @param map The map.
 * @param usable Whether a layer may be picked from (visible and unlocked).
 * @param sizeOf Frame size lookup.
 * @param opaque Frame pixel opacity lookup.
 * @param tileSize Tile size in pixels.
 * @param wx World pixel x.
 * @param wy World pixel y.
 * @param options The frame each prop is shown with, which props may be hit and which presence drives.
 * @returns The props, empty when none lies under the point or it is off the map.
 */
export function propsAt(map: MapDocument, usable: (layerId: string) => boolean, sizeOf: FrameSizeOf, opaque: OpaqueAt, tileSize: number, wx: number, wy: number, options: PickOptions = {}): { readonly layerId: string; readonly propId: string }[] {
  if (wx < 0 || wy < 0 || wx >= map.width * tileSize || wy >= map.height * tileSize) {
    return [];
  }
  const hits: { readonly layerId: string; readonly propId: string }[] = [];
  for (let i = map.layers.length - 1; i >= 0; i -= 1) {
    const layer = map.layers[i] as LayerDocument;
    if (layer.kind !== "prop" || !usable(layer.id)) {
      continue;
    }
    const pick = forLayer(layer.id, options);
    const rowOf = (prop: PropInstance): number => propRowAt(prop, sizeOf, tileSize);
    const under = keyed(layer.id, layer.props, options).filter((prop) => propAt([prop], sizeOf, opaque, tileSize, wx, wy, pick) !== undefined);
    under.sort((a, b) => rowOf(b) - rowOf(a) || (b.order as number) - (a.order as number));
    hits.push(...under.map((prop) => ({ layerId: layer.id, propId: prop.id })));
  }
  return hits;
}

/**
 * The first hit among the usable layers of one kind, searched from the top of the draw order down.
 *
 * @param layers The layers in draw order.
 * @param usable Layer filter.
 * @param kind Layer kind to search.
 * @param test Hit test for one layer of that kind.
 * @returns The hit, or null.
 */
function topmost<K extends LayerDocument["kind"]>(layers: readonly LayerDocument[], usable: (layerId: string) => boolean, kind: K, test: (layer: Extract<LayerDocument, { kind: K }>) => PickHit | null): PickHit | null {
  for (let i = layers.length - 1; i >= 0; i -= 1) {
    const layer = layers[i] as LayerDocument;
    if (layer.kind !== kind || !usable(layer.id)) {
      continue;
    }
    const hit = test(layer as Extract<LayerDocument, { kind: K }>);
    if (hit !== null) {
      return hit;
    }
  }
  return null;
}
